import React, { useEffect, useRef } from 'react';
import { Archive, Download, Edit3, ExternalLink, File, FileAudio, FileCode, FileImage, FileSpreadsheet, FileText, FileVideo, Folder, FolderInput, Link as LinkIcon, Trash2 } from 'lucide-react';
import { getFileCategory } from '@/lib/file-category';
import { SmallThreeDotMenuButton, type ThreeDotMenuItem } from '@/styles/StandardButtons';
import type { DriveItem } from '../types';

export interface DriveItemActions {
  onOpen: (item: DriveItem) => void;
  onRename: (item: DriveItem) => void;
  onDownload: (item: DriveItem) => void;
  onMove: (item: DriveItem) => void;
  onDelete: (item: DriveItem) => void;
  /** A client / vendor only opens and downloads what its shared folder holds: no rename, move or delete. */
  readOnly?: boolean;
}

// category -> icon is the only Drive-specific part; the classification itself is the shared `getFileCategory`.
const CATEGORY_ICONS: Record<ReturnType<typeof getFileCategory>, React.ComponentType<{ className?: string }>> = {
  image: FileImage,
  video: FileVideo,
  audio: FileAudio,
  pdf: FileText,
  excel: FileSpreadsheet,
  word: FileText,
  powerpoint: FileText,
  archive: Archive,
  text: FileCode,
  other: File,
};

export function DriveItemIcon({ item, className = 'w-4 h-4 text-gray-400' }: { item: DriveItem; className?: string }) {
  if (item.type === 'folder') return <Folder className={className} />;
  if (item.type === 'url') return <LinkIcon className={className} />;
  const Icon = CATEGORY_ICONS[getFileCategory(item.mime_type, item.extension)];
  return <Icon className={className} />;
}

export function DriveItemMenu({ item, actions }: { item: DriveItem; actions: DriveItemActions }) {
  // a default shared folder is fixed (the server refuses it too)
  const locked = actions.readOnly || !!item.system_key;
  const menu: ThreeDotMenuItem[] = [
    { label: item.type === 'folder' ? 'Open' : item.type === 'url' ? 'Open Link' : 'Open', icon: item.type === 'url' ? <ExternalLink className="w-3.5 h-3.5" /> : <File className="w-3.5 h-3.5" />, onClick: () => actions.onOpen(item) },
    ...(locked ? [] : [{ label: item.type === 'url' ? 'Edit' : 'Rename', icon: <Edit3 className="w-3.5 h-3.5" />, onClick: () => actions.onRename(item) }]),
    ...(item.type === 'file' ? [{ label: 'Download', icon: <Download className="w-3.5 h-3.5" />, onClick: () => actions.onDownload(item) }] : []),
    ...(!locked ? [{ label: 'Move', icon: <FolderInput className="w-3.5 h-3.5" />, onClick: () => actions.onMove(item) }] : []),
    ...(!locked ? [{ label: 'Delete', icon: <Trash2 className="w-3.5 h-3.5" />, onClick: () => actions.onDelete(item), destructive: true }] : []),
  ];
  return (
    <div onClick={(e) => e.stopPropagation()}>
      <SmallThreeDotMenuButton items={menu} />
    </div>
  );
}

/** none / some (indeterminate) / all of the VISIBLE items. */
export function SelectAllCheckbox({ total, selected, onChange }: { total: number; selected: number; onChange: (all: boolean) => void }) {
  const ref = useRef<HTMLInputElement>(null);
  const all = total > 0 && selected === total;
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = selected > 0 && !all;
  }, [selected, all]);
  return (
    <input
      ref={ref}
      type="checkbox"
      aria-label="Select all"
      checked={all}
      disabled={total === 0}
      onChange={(e) => onChange(e.target.checked)}
      className="h-3.5 w-3.5 text-blue-600 rounded focus:ring-blue-500 border-gray-300"
    />
  );
}
